import moment from 'moment';
import validator from 'validator';
import _ from 'lodash';

const FIELD_TYPES = {
  TEXT: 'string',
  NUMBER: 'number',
  BOOLEAN: 'boolean',
  IMAGE: 'image',
  DATE: 'date',
  DATETIME: 'datetime'
};

/*
    = , !== , >, >=, < , <= , IS_EMPTY, IS_NOT_EMPTY, IN, NOT_IN, STARTS_WITH, 
    DOES_NOT_START_WITH, ENDS_WITH, DOES_NOT_END_WITH,
    CONTAINS, DOES_NOT_CONTAIN, ON_DATE, NOT_ON_DATE, AFTER, BEFORE
*/
const conditionMatches = ({
  condition,
  document,
  contentType,
  ignoreField
}) => {
  try {
    const field = condition.field;

    if (!contentType) {
      return false;
    }

    let contentTypeField =
      field === 'id'
        ? { type: 'string' }
        : contentType.fields.find(f => f.name === field);

    if (!ignoreField && !contentTypeField) {
      return false;
    }

    let value = condition.value;
    let documentValue = _.get(document, condition.field);

    if (ignoreField) {
      contentTypeField = { type: '' };
    }

    // Override field type with assumptions by operator
    if (contentTypeField) {
      if (['>', '>=', '<', '<='].some(o => o === condition.operator)) {
        contentTypeField.type = FIELD_TYPES.NUMBER;
      } else if (
        ['True', 'False', 'true', 'false', true, false].some(
          o => o === condition.value
        )
      ) {
        contentTypeField.type = FIELD_TYPES.BOOLEAN;
      }
    }

    switch (contentTypeField.type) {
      case FIELD_TYPES.BOOLEAN: {
        if (value === 'true') {
          value = true;
        } else if (value === 'false') {
          value = false;
        }

        if (documentValue === 'true') {
          documentValue = true;
        } else if (value === 'false') {
          documentValue = false;
        }
        break;
      }
      case FIELD_TYPES.DATE: {
        if (
          !Number.isNaN(Date.parse(value)) &&
          !Number.isNaN(Date.parse(documentValue))
        ) {
          value = Date.parse(value);
          documentValue = Date.parse(value);
        }
        break;
      }
      case FIELD_TYPES.DATETIME: {
        if (value?.seconds) {
          value = value.toDate();
        }
        if (documentValue?.seconds) {
          documentValue = documentValue.toDate();
        }
        if (
          !Number.isNaN(Date.parse(value)) &&
          !Number.isNaN(Date.parse(documentValue))
        ) {
          value = Date.parse(value);
          documentValue = Date.parse(documentValue);
          break;
        } else {
          return false;
        }
      }
      case FIELD_TYPES.NUMBER: {
        if (
          !Number.isNaN(parseInt(value)) &&
          !Number.isNaN(parseInt(documentValue))
        ) {
          value = parseFloat(value);
          documentValue = parseFloat(documentValue);
          break;
        } else {
          return false;
        }
      }
      case FIELD_TYPES.IMAGE: {
        break;
      }
      default:
        // TEXT
        if (
          value &&
          value.toLowerCase &&
          documentValue &&
          documentValue.toLowerCase &&
          !Array.isArray(value) &&
          !Array.isArray(documentValue)
        ) {
          value = value.toLowerCase().trim();
          documentValue = documentValue.toLowerCase().trim();
        }
        break;
    }

    if (
      ['>', '>=', '<', '<='].some(o => o === condition.operator) &&
      contentTypeField.type !== FIELD_TYPES.NUMBER
    ) {
      return false;
    }

    let operator = condition.operator ? condition.operator.toUpperCase() : '';
    if (
      ['EQUALS', 'IS_EQUAL_TO', 'EQUAL_TO', '=', '=='].some(o => o === operator)
    ) {
      operator = '=';
    } else if (
      ['DOES_NOT_EQUAL', 'NOT_EQUAL_TO', '!=', '!==', '<>'].some(
        o => o === operator
      )
    ) {
      operator = '!==';
    } else if (['GREATER_THAN', '>'].some(o => o === operator)) {
      operator = '>';
    } else if (['GREATER_THAN_OR_EQUAL', '>='].some(o => o === operator)) {
      operator = '>=';
    } else if (['LESS_THAN', '<'].some(o => o === operator)) {
      operator = '<';
    } else if (['LESS_THAN_OR_EQUAL', '<='].some(o => o === operator)) {
      operator = '<=';
    } else if (['IS_EMPTY', 'EMPTY'].some(o => o === operator)) {
      operator = 'IS_EMPTY';
    } else if (['IS_NOT_EMPTY', 'NOT_EMPTY'].some(o => o === operator)) {
      operator = 'IS_NOT_EMPTY';
    }

    switch (operator.toUpperCase()) {
      case 'MINLENGTH': {
        return validator.isLength(documentValue, { min: parseInt(value) });
      }
      case 'MAXLENGTH': {
        return validator.isLength(documentValue, { max: parseInt(value) });
      }
      case 'EMAIL': {
        try {
          return /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(
            documentValue
          );
        } catch {
          return false;
        }
      }
      case 'FQDN': {
        return validator.isFQDN(value);
      }
      case 'POSTCODE': {
        const postCodes = /^[a-zA-Z]{1,2}\d[a-zA-Z\d]? \d[a-zA-Z]{2}$/;
        const zipCodes = /^\d{5}(?:-\d{4})?$/;
        return postCodes.test(documentValue) || zipCodes.test(documentValue);
      }
      case 'STRONGPASSWORD': {
        return validator.isStrongPassword(documentValue, {
          minLength: 8,
          minLowercase: 1,
          minUppercase: 1,
          minNumbers: 1,
          minSymbols: 1
        });
      }
      case 'FORMATTEDPHONE': {
        return /^(?:\+(?:\d{1,3}))?(?:\s*\(\d{1,}\))?(?:[-\s]*\d{1,}\s*){3,}/.test(
          documentValue
        );
      }
      case 'STARTS_WITH': {
        if (!documentValue?.toString().indexOf) {
          return false;
        }
        if (Array.isArray(value)) {
          return value.some(
            c =>
              documentValue
                .toString()
                .toLowerCase()
                .indexOf(c.toString().toLowerCase()) === 0
          );
        } else {
          return (
            documentValue
              .toString()
              .toLowerCase()
              .indexOf(value.toString().toLowerCase()) === 0
          );
        }
      }
      case 'DOES_NOT_START_WITH': {
        if (!documentValue?.toString().indexOf) {
          return false;
        }
        if (Array.isArray(value)) {
          return value.some(
            c =>
              documentValue
                .toString()
                .toLowerCase()
                .indexOf(c.toString().toLowerCase()) !== 0
          );
        } else {
          return (
            documentValue
              .toString()
              .toLowerCase()
              .indexOf(value.toString().toLowerCase()) !== 0
          );
        }
      }
      case 'ENDS_WITH': {
        if (!documentValue?.toString().indexOf) {
          return false;
        }
        if (Array.isArray(value)) {
          return value.some(c =>
            documentValue
              .toString()
              .toLowerCase()
              .endsWith(c.toString().toLowerCase())
          );
        } else {
          return documentValue
            .toString()
            .toLowerCase()
            .endsWith(value.toString().toLowerCase());
        }
      }
      case 'DOES_NOT_END_WITH': {
        if (!documentValue?.toString().indexOf) {
          return false;
        }
        if (Array.isArray(value)) {
          return !value.some(c =>
            documentValue
              .toString()
              .toLowerCase()
              .endsWith(c.toString().toLowerCase())
          );
        } else {
          return !documentValue
            .toString()
            .toLowerCase()
            .endsWith(value.toString().toLowerCase());
        }
      }
      case 'CONTAINS': {
        if (!documentValue?.toString().indexOf) {
          return false;
        }
        if (Array.isArray(value)) {
          return value.some(
            c =>
              documentValue
                .toString()
                .toLowerCase()
                .indexOf(c.toString().toLowerCase()) > -1
          );
        } else {
          return (
            documentValue
              .toString()
              .toLowerCase()
              .indexOf(value.toString().toLowerCase()) > -1
          );
        }
      }
      case 'DOES_NOT_CONTAIN': {
        if (!documentValue?.toString().indexOf) {
          return false;
        }
        if (Array.isArray(value)) {
          return !value.some(
            c =>
              documentValue
                .toString()
                .toLowerCase()
                .indexOf(c.toString().toLowerCase()) > -1
          );
        } else {
          return (
            documentValue
              .toString()
              .toLowerCase()
              .indexOf(value.toString().toLowerCase()) === -1
          );
        }
      }
      case 'ON_DATE': {
        return moment(value).isSame(documentValue);
      }
      case 'NOT_ON_DATE': {
        return !moment(value).isSame(documentValue);
      }
      case 'AFTER': {
        return moment(documentValue).isAfter(value);
      }
      case 'ON_OR_AFTER': {
        return moment(documentValue).isSameOrAfter(value);
      }
      case 'BEFORE': {
        return moment(documentValue).isBefore(value);
      }
      case 'ON_OR_BEFORE': {
        return moment(documentValue).isSameOrBefore(value);
      }
      case 'IN': {
        let items;
        try {
          if (
            !Array.isArray(value) &&
            value?.indexOf &&
            value.indexOf(',') > -1
          ) {
            items = value?.split(',') || [];
          } else {
            items = Array.isArray(value) ? value : JSON.parse(value);
          }

          if (Array.isArray(documentValue)) {
            return items.some(v =>
              documentValue.some(
                d =>
                  d
                    .toString()
                    .toLowerCase()
                    .trim() ===
                  v
                    ?.toString()
                    .toLowerCase()
                    .trim()
              )
            );
          }
          return items.some(
            v =>
              v
                ?.toString()
                .toLowerCase()
                .trim() ===
              documentValue
                ?.toString()
                .toLowerCase()
                .trim()
          );
        } catch {
          return false;
        }
      }
      case 'NOT_IN': {
        let items;
        try {
          if (!Array.isArray(value) && value && value.indexOf(',') > -1) {
            items = value?.split(',') || [];
          } else {
            items = Array.isArray(value) ? value : JSON.parse(value);
          }

          if (Array.isArray(documentValue)) {
            return !items.some(v =>
              documentValue.some(
                d =>
                  d
                    .toString()
                    .toLowerCase()
                    .trim() ===
                  v
                    ?.toString()
                    .toLowerCase()
                    .trim()
              )
            );
          }
          return !items.some(
            v =>
              v
                ?.toString()
                .toLowerCase()
                .trim() ===
              documentValue
                ?.toString()
                .toLowerCase()
                .trim()
          );
        } catch {
          return false;
        }
      }
      case 'IS_EMPTY': {
        if (contentTypeField.type === FIELD_TYPES.IMAGE) {
          return documentValue.length === 0;
        }
        return documentValue === undefined || documentValue === '';
      }
      case 'IS_NOT_EMPTY': {
        if (contentTypeField.type === FIELD_TYPES.IMAGE) {
          return documentValue.length > 0;
        }
        if (field.toLowerCase() === 'any') {
          const items = value.split(',');
          return items.every(
            i =>
              document[i] !== undefined &&
              document[i] !== null &&
              document[i] !== ''
          );
        } else {
          return (
            documentValue !== undefined &&
            documentValue !== '' &&
            documentValue !== null
          );
        }
      }
      case '>': {
        return documentValue > value;
      }
      case '>=': {
        return documentValue >= value;
      }
      case '<': {
        return documentValue < value;
      }
      case '<=': {
        return documentValue <= value;
      }
      case '!==': {
        return documentValue !== value;
      }

      default:
        // '='
        return documentValue === value;
    }
  } catch (err) {
    console.error('conditionMatches', condition, err);
    return false;
  }
};

const getContentTypeFromDataLinkId = ({ dataLinkId, types, dataLinks }) => {
  const dataLink = dataLinks.find(d => d.id === dataLinkId);
  if (!dataLink && dataLinkId !== 'user') {
    return {
      error: `a data link with id ${dataLinkId} was not found`
    };
  }

  const contentType = types?.find(t => {
    if (dataLinkId === 'user') {
      return t.type === 'user';
    }
    return t.type === dataLink.type;
  });

  if (!contentType) {
    return {
      error: `a content type of type ${
        dataLinkId === 'user' ? 'user' : dataLink.type
      } was not found in app context`
    };
  }

  return contentType;
};

export { conditionMatches, getContentTypeFromDataLinkId };
